import { validateCertificationGroups } from 'modules/views/pages/Certifications/utils/certificationValidation';
import { WIZARD_STEPS, StepKey } from './config';

import { WorkerProfileField } from 'types';
import { WorkerInvitationStatus } from 'models/general';
import { hasWorkerAcceptedTheProject } from 'utils/onboarding';
import { ROUTES } from 'constants/routes';
import { SkippableTillType } from 'utils/skippableFieldsUtils';
import { getFirstIncompleteConsentRoute, getAcceptedCustomConsentIds } from 'utils/consentSteps';
import { ConsentAgreementKind } from 'constants/common';

const fieldHasValidValue = (value: any, valueType: string): boolean => {
  if (valueType === 'string' && (!value || value?.trim?.() === '')) {
    return false;
  } else if (valueType === 'number' && (value === null || value === undefined)) {
    return false;
  } else if (valueType === 'object' && Object.keys(value || {}).length === 0) {
    return false;
  } else if (valueType === 'boolean' && (value === null || value === undefined)) {
    return false;
  } else if (valueType === 'array' && (!Array.isArray(value) || value.length === 0)) {
    return false;
  }
  return true;
};

const isFieldMandatoryAndNotSkippable = (constraints: any): boolean => {
  return (
    constraints?.isMandatory &&
    (constraints?.skippableTill === SkippableTillType.NotSet ||
      constraints?.skippableTill === SkippableTillType.ConsentForm)
  );
};

const personalInformationFields = [
  {
    constraintKey: WorkerProfileField.workerFirstName,
    valueKey: 'firstName',
    valueType: 'string',
  },
  {
    constraintKey: WorkerProfileField.workerMiddleName,
    valueKey: 'middleName',
    valueType: 'string',
  },
  {
    constraintKey: WorkerProfileField.workerLastName,
    valueKey: 'lastName',
    valueType: 'string',
  },
  {
    constraintKey: WorkerProfileField.workerDateOfBirth,
    valueKey: 'dateOfBirth',
    valueType: 'string',
  },
  {
    constraintKey: WorkerProfileField.workerGender,
    valueKey: 'gender',
    valueType: 'number',
  },
  {
    constraintKey: WorkerProfileField.workerEthnicity,
    valueKey: 'ethnicity',
    valueType: 'object',
  },
  {
    constraintKey: WorkerProfileField.workerPrimaryLanguage,
    valueKey: 'primaryLanguage',
    valueType: 'object',
  },
  {
    constraintKey: WorkerProfileField.workerVeteranStatus,
    valueKey: 'isVeteran',
    valueType: 'boolean',
  },
  {
    constraintKey: WorkerProfileField.workerAllergies,
    valueKey: 'allergies',
    valueType: 'string',
  },
  {
    constraintKey: WorkerProfileField.workerLastSSN,
    valueKey: 'socialSecurityNumber',
    valueType: 'string',
  },
  {
    constraintKey: WorkerProfileField.workerGovtIdType,
    valueKey: 'identificationType',
    valueType: 'object',
  },
  {
    constraintKey: WorkerProfileField.workerGovtIdIssuedBy,
    valueKey: 'identificationGeographicLocation',
    valueType: 'object',
  },
  {
    constraintKey: WorkerProfileField.workerGovtIdNumber,
    valueKey: 'identificationNumber',
    valueType: 'string',
  },
  {
    constraintKey: WorkerProfileField.workerGovtIdImage,
    valueKey: 'governmentDocumentFiles',
    valueType: 'array',
  },
];

const addressFields = [
  {
    constraintKey: WorkerProfileField.homeAddressLine1,
    valueKey: 'line1',
    valueType: 'string',
  },
  {
    constraintKey: WorkerProfileField.homeAddressLine2,
    valueKey: 'line2',
    valueType: 'string',
  },
  {
    constraintKey: WorkerProfileField.homeAddressCity,
    valueKey: 'city',
    valueType: 'string',
  },
  {
    constraintKey: WorkerProfileField.homeAddressStateProvince,
    valueKey: 'stateName',
    valueType: 'string',
  },
  {
    constraintKey: WorkerProfileField.homeAddressZipPostal,
    valueKey: 'zipCode',
    valueType: 'string',
  },
  {
    constraintKey: WorkerProfileField.homeAddressCountry,
    valueKey: 'country',
    valueType: 'object',
  },
];

const contactInformationFields = [
  {
    constraintKey: WorkerProfileField.email,
    valueKey: 'email',
    valueType: 'string',
  },
  {
    constraintKey: WorkerProfileField.mobilePhoneNumber,
    valueKey: 'mobilePhoneNumber',
    valueType: 'string',
  },
  {
    constraintKey: WorkerProfileField.alternateContactMobilePhoneNumber,
    valueKey: 'phoneNumber',
    valueType: 'string',
  },
  {
    constraintKey: WorkerProfileField.emergencyContactName,
    valueKey: 'emergencyContactName',
    valueType: 'string',
  },
  {
    constraintKey: WorkerProfileField.emergencyContactRelationship,
    valueKey: 'emergencyContactRelationship',
    valueType: 'string',
  },
  {
    constraintKey: WorkerProfileField.emergencyContactMobilePhone,
    valueKey: 'emergencyContactPhone',
    valueType: 'string',
  },
];

const tradesFields = [
  {
    constraintKey: WorkerProfileField.workerRole,
    valueKey: 'isSupervisor',
    valueType: 'boolean',
  },
  {
    constraintKey: WorkerProfileField.workerLaborUnionStatus,
    valueKey: 'isAffiliatedToLaborUnion',
    valueType: 'boolean',
  },
  {
    constraintKey: WorkerProfileField.workerWorkExperience,
    valueKey: 'isSkilled',
    valueType: 'boolean',
  },
  {
    constraintKey: WorkerProfileField.workerLaborUnionNumber,
    valueKey: 'laborUnionNumber',
    valueType: 'string',
  },
  {
    constraintKey: WorkerProfileField.workerTrades,
    valueKey: 'trades',
    valueType: 'array',
  },
];

export const isStepComplete = ({
  stepKey,
  onboarding,
  profileFieldConstraintsMap,
  canCreateUnverifiedWorkers,
  consentAgreements,
}: {
  stepKey: StepKey;
  onboarding: any;
  profileFieldConstraintsMap: any;
  canCreateUnverifiedWorkers?: boolean;
  consentAgreements?: any[];
}): boolean => {
  //* Check if user has registered or not. If not then all the steps are incomplete.
  //* An existing worker missing a mandatory email/mobile is caught by the Contact Information step instead.
  if (!onboarding?.id) {
    if (profileFieldConstraintsMap?.[WorkerProfileField?.email]?.isMandatory && !onboarding?.email) {
      return false;
    }
    if (
      profileFieldConstraintsMap?.[WorkerProfileField?.mobilePhoneNumber]?.isMandatory &&
      !onboarding?.mobilePhoneNumber
    ) {
      return false;
    }
    if (
      profileFieldConstraintsMap?.[WorkerProfileField?.emailOrMobilePhoneNumber]?.isMandatory &&
      !onboarding?.mobilePhoneNumber &&
      !onboarding?.email
    ) {
      return false;
    }
  }
  //* Password is not set
  if (
    !canCreateUnverifiedWorkers &&
    onboarding?.invitationStatus !== WorkerInvitationStatus.ACCEPTED &&
    onboarding?.invitationStatus !== WorkerInvitationStatus.NOT_INVITED
  ) {
    return false;
  }
  //* Worker not created
  if (!onboarding?.id && !onboarding?.uploadingDetails?.isOffline) {
    return false;
  }

  if (stepKey === StepKey.REGISTRATION) {
    // The registration group owns the consent sub-steps (Terms → Standard Consent → Biometric →
    // Signature) and is complete only once the signature is captured. This now applies offline too:
    // the offline signature path (RegistrationSignature) sets `signatureUrl` to the drawn PNG, and
    // the route guard's consent gate never checked `isOffline` anyway — so a blanket offline
    // short-circuit here made step-completion disagree with the guard.
    //
    // Backwards compatibility: records persisted by older builds can have no `signatureUrl` at all.
    // Trust a completion that was already recorded for them rather than stranding them permanently
    // INCOMPLETE (the terminal check in uploadManager requires every section to be true).
    if (
      onboarding?.uploadingDetails?.isOffline &&
      onboarding?.uploadingDetails?.sectionsCompleted?.[StepKey.REGISTRATION] === true &&
      !onboarding?.uploadingDetails?.pendingUploads?.signature
    ) {
      return true;
    }
    return !!onboarding?.signatureUrl;
  } else if (stepKey === StepKey.TAKE_PHOTO) {
    if (
      isFieldMandatoryAndNotSkippable(profileFieldConstraintsMap?.[WorkerProfileField.workerProfilePhoto]) &&
      !onboarding?.pictureUrl
    ) {
      return false;
    }
  } else if (stepKey === StepKey.PERSONAL_INFORMATION) {
    for (const field of personalInformationFields) {
      if (
        isFieldMandatoryAndNotSkippable(profileFieldConstraintsMap?.[field.constraintKey]) &&
        !fieldHasValidValue(onboarding?.[field.valueKey], field.valueType)
      ) {
        return false;
      }
    }
  } else if (stepKey === StepKey.CONTACT_INFORMATION) {
    //* Check address fields
    for (const field of addressFields) {
      if (
        isFieldMandatoryAndNotSkippable(profileFieldConstraintsMap?.[field.constraintKey]) &&
        !fieldHasValidValue(onboarding?.address?.[field.valueKey], field.valueType)
      ) {
        return false;
      }
    }
    //* Check other contact information fields
    for (const field of contactInformationFields) {
      if (
        isFieldMandatoryAndNotSkippable(profileFieldConstraintsMap?.[field.constraintKey]) &&
        !fieldHasValidValue(onboarding?.[field.valueKey], field.valueType)
      ) {
        return false;
      }
    }
  } else if (stepKey === StepKey.TRADES) {
    for (const field of tradesFields) {
      if (
        isFieldMandatoryAndNotSkippable(profileFieldConstraintsMap?.[field.constraintKey]) &&
        !fieldHasValidValue(onboarding?.[field.valueKey], field.valueType)
      ) {
        return false;
      }
    }
  } else if (stepKey === StepKey.CERTIFICATIONS) {
    if (
      validateCertificationGroups(onboarding.certificationGroups || [], onboarding.allCertifications?.items || [])
        .length > 0
    ) {
      return false;
    }
  } else if (stepKey === StepKey.CONSENT_FORM) {
    if (!hasWorkerAcceptedTheProject(onboarding?.assignedCompanies)) {
      return false;
    }
    // In addEmployerFlow only: unsigned custom forms also block step completion.
    // In the normal registration flow, project acceptance alone marks this step done.
    if (onboarding?.temp?.addEmployerFlow) {
      const acceptedIds = getAcceptedCustomConsentIds(onboarding);
      const hasUnsigned = (consentAgreements || []).some(
        a => a.kind === ConsentAgreementKind.Custom && !acceptedIds.has(a.consentAgreementContentId),
      );
      if (hasUnsigned) return false;
    }
  } else {
    return true;
  }

  return true; // All required fields and sub-steps are complete
};

export const areAllStepsComplete = ({
  onboarding,
  profileFieldConstraintsMap,
  canCreateUnverifiedWorkers,
  consentAgreements,
}: {
  onboarding: any;
  profileFieldConstraintsMap: any;
  canCreateUnverifiedWorkers?: boolean;
  consentAgreements?: any[];
}): boolean => {
  for (const step of WIZARD_STEPS) {
    if (
      !isStepComplete({
        stepKey: step.key as StepKey,
        onboarding,
        profileFieldConstraintsMap,
        canCreateUnverifiedWorkers,
        consentAgreements,
      })
    ) {
      return false; // Found an incomplete step
    }
  }
  return true; // All steps are complete
};

export const getFirstIncompleteStep = ({
  onboarding,
  profileFieldConstraintsMap,
  canCreateUnverifiedWorkers,
  consentAgreements,
}: {
  onboarding: any;
  profileFieldConstraintsMap: any;
  canCreateUnverifiedWorkers?: boolean;
  consentAgreements?: any[];
}): any => {
  for (let i = 0; i < WIZARD_STEPS.length; i++) {
    // Skipped steps (e.g. the standalone Biometric Consent step replaced by the
    // registration sub-step) are never "first incomplete".
    if (WIZARD_STEPS[i].shouldSkip?.({ cache: { profileFieldConstraintsMap } } as any)) {
      continue;
    }
    if (
      !isStepComplete({
        stepKey: WIZARD_STEPS[i].key as StepKey,
        onboarding,
        profileFieldConstraintsMap,
        canCreateUnverifiedWorkers,
        consentAgreements,
      })
    ) {
      if (
        (onboarding?.id || onboarding?.uploadingDetails?.isOffline) &&
        !canCreateUnverifiedWorkers &&
        onboarding?.invitationStatus !== WorkerInvitationStatus.ACCEPTED &&
        onboarding?.invitationStatus !== WorkerInvitationStatus.NOT_INVITED
      ) {
        return { ...WIZARD_STEPS[i], route: ROUTES.CREATE_PASSWORD.path }; // Redirect to password creation screen if the password is not set
      }
      // Registration is incomplete only because the consent sub-steps / signature
      // are still pending → route to the first incomplete consent sub-step.
      if (WIZARD_STEPS[i].key === StepKey.REGISTRATION) {
        return {
          ...WIZARD_STEPS[i],
          route: getFirstIncompleteConsentRoute(consentAgreements || [], onboarding) || ROUTES.TERMS_AND_CONDITIONS.path,
        };
      }
      return WIZARD_STEPS[i]; // Return the first incomplete step
    }
  }
  return null; // All steps are complete
};

export const getSectionsCompleted = ({
  onboarding,
  profileFieldConstraintsMap,
  canCreateUnverifiedWorkers,
  consentAgreements,
}: {
  onboarding: any;
  profileFieldConstraintsMap: any;
  canCreateUnverifiedWorkers?: boolean;
  consentAgreements?: any[];
}): { [key in StepKey]?: boolean } => {
  let sectionsCompleted = {};
  for (let i = 0; i < WIZARD_STEPS.length; i++) {
    sectionsCompleted[WIZARD_STEPS[i].key as StepKey] = isStepComplete({
      stepKey: WIZARD_STEPS[i].key as StepKey,
      onboarding,
      profileFieldConstraintsMap,
      canCreateUnverifiedWorkers,
      consentAgreements,
    });
  }
  return sectionsCompleted;
};
